import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";

import { dossierExportsAdapter, initialState } from "./state";

import { caseManagerApi } from "services/streamingApi/caseManager.api";
import { DossierExportsForDossierPaginatedRequest } from "types/DossierExportsForDossierPaginatedRequest";
import { DossierExport } from "types/DossierExport";

// thunks
const loadDossierExportsForDossierAsync = createAsyncThunk(
    "caseExports/loadDossierExportsForDossierAsync",
    async (
        paginatedDossierExportsRequest: DossierExportsForDossierPaginatedRequest,
        { rejectWithValue, dispatch },
    ) => {
        const { data, error, isSuccess } = await dispatch(
            caseManagerApi.endpoints.getDossierExportsForDossier.initiate(
                paginatedDossierExportsRequest,
            ),
        );

        if (isSuccess) {
            return data;
        }

        return rejectWithValue(
            `Error while retrieving dossier exports: ${JSON.stringify(error)}`,
        );
    },
);

// slice
export const caseExportsSlice = createSlice({
    name: "caseExports",
    initialState,
    reducers: {
        updateDossierExport: (
            state,
            action: PayloadAction<{
                dossierExportChanged: DossierExport;
            }>,
        ) => {
            if (action.payload.dossierExportChanged) {
                dossierExportsAdapter.updateOne(state.dossierExports, {
                    id: action.payload.dossierExportChanged.id,
                    changes: {
                        ...action.payload.dossierExportChanged,
                    },
                });
            }
        },
    },
    extraReducers(builder) {
        builder.addCase(loadDossierExportsForDossierAsync.pending, (state) => {
            dossierExportsAdapter.removeAll(state.dossierExports);

            state.totalDossierExports = undefined;
            state.isLoadingDossierExports = true;
            state.errorLoadingDossierExports = undefined;
        });
        builder.addCase(
            loadDossierExportsForDossierAsync.fulfilled,
            (state, action) => {
                dossierExportsAdapter.setAll(
                    state.dossierExports,
                    action.payload.data,
                );

                state.totalDossierExports = action.payload.totalRecords;
                state.isLoadingDossierExports = false;
            },
        );
        builder.addCase(
            loadDossierExportsForDossierAsync.rejected,
            (state, action) => {
                state.isLoadingDossierExports = false;
                state.errorLoadingDossierExports = action.error.message;
            },
        );
    },
});

// exports
export const caseExportsThunks = { loadDossierExportsForDossierAsync };

export const caseExportsActions = caseExportsSlice.actions;

export default caseExportsSlice.reducer;
